import * as React from "react";
import {
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { useTitle } from "../../hooks/useTitle";
import { useGetFinanceReportQuery } from "../../services/report.service";
import { FinancialReport } from "../../types/report";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { useGetProjectsQuery } from "../../services/project.service";
import { CardBase, GlassDataGrid, StyledTextField } from "../../components/global";
import useDebounce from "../../hooks/useDebounce";

const initStartDate = dayjs().startOf("month").toDate();
const initEndDate = dayjs().toDate();

const TableToolbar = ({
  onFilter,
  onSearch,
}: {
  onFilter: (startDate: Dayjs | Date, endDate: Dayjs | Date) => void;
  onSearch: (query?: string) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [query, setQuery] = React.useState<string>("");
  const [start, setStart] = React.useState<Dayjs | Date>(initStartDate);
  const [end, setEnd] = React.useState<Dayjs | Date>(initEndDate);
  const debouncedQuery = useDebounce(query, 500);

  React.useEffect(() => {
    onFilter(start, end);
  }, [start, end, onFilter]);

  React.useEffect(() => {
    onSearch(debouncedQuery ? debouncedQuery.trim() : undefined);
  }, [debouncedQuery, onSearch]);

  return (
    <GridToolbarContainer
      sx={{
        padding: { xs: "12px 16px", sm: "16px 20px" },
        marginBottom: 0,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: { xs: "flex-start", sm: "space-between" },
        gap: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          display: "none",
        }}
      >
        <DatePicker
          label="Start"
          format="MMM DD, YYYY"
          value={dayjs(start)}
          sx={{
            mt: { xs: 0, sm: 1 },
            width: { xs: "100%", sm: 200 },
            flex: { xs: 1, sm: "none" },
          }}
          onChange={(value) => setStart(value || new Date())}
        />

        <Box
          sx={{
            ml: { xs: 0, sm: 1 },
            mr: { xs: 0, sm: 1 },
            alignSelf: { xs: "center", sm: "auto" },
            whiteSpace: "nowrap",
          }}
        >
          to
        </Box>
        <DatePicker
          label="End"
          format="MMM DD, YYYY"
          value={dayjs(end)}
          sx={{
            mt: { xs: 0, sm: 1 },
            width: { xs: "100%", sm: 200 },
            flex: { xs: 1, sm: "none" },
          }}
          onChange={(value) => setEnd(value || new Date())}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <StyledTextField
          margin="dense"
          label="Search keyword..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{
            minWidth: { xs: "100%", sm: 220 },
            flex: 1,
          }}
        />
        {!!query && (
          <Button
            variant="text"
            sx={{
              whiteSpace: "nowrap",
            }}
            onClick={() => setQuery("")}
          >
            Reset
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{ allColumns: true, fileName: "Finance Report" }}
        />
      </Box>
    </GridToolbarContainer>
  );
};

const FinanceReportPage = () => {
  useTitle("Finance Report");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: dataProjects, isLoading: isLoadingDataProjects } =
    useGetProjectsQuery({
      pageSize: 1000,
    });
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [filterDate, setFilterDate] = React.useState<
    [Dayjs | Date, Dayjs | Date]
  >([initStartDate, initEndDate]);
  const [searchQuery, setSearchQuery] = React.useState<string>("");

  const { data, isLoading, isFetching } = useGetFinanceReportQuery({
    start_date: dayjs(filterDate[0]).format("YYYY-MM-DD"),
    end_date: dayjs(filterDate[1]).format("YYYY-MM-DD"),
  });

  const rows: GridRowsProp = React.useMemo(
    () =>
      data?.map((it, _id) => ({
        ...it,
        id: _id,
      })) || [],
    [data]
  );

  const filteredRows: GridRowsProp = React.useMemo(() => {
    if (!searchQuery) return rows;
    const keyword = searchQuery.toLowerCase();
    return rows.filter((row) => {
      const combined =
        `${row.title || ""} ${row.location_name || ""} ${row.talent_name || ""} ${row.manager_name || ""} ${row.type || ""}`
          .toLowerCase();
      return combined.includes(keyword);
    });
  }, [rows, searchQuery]);

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "job",
        headerName: "Work ID# - Work Title",
        minWidth: isMobile ? 180 : 300,
        valueGetter: (params: GridValueGetterParams) => `#${params.row.job_id} ${params.row.title}`,
      },
      {
        field: "location",
        headerName: "Work Location",
        minWidth: isMobile ? 140 : 200,
        valueGetter: (params: GridValueGetterParams) => `${params.row.location_name}`,
      },
      // {
      //   field: "site_number",
      //   headerName: "Site Name/#",
      //   minWidth: 150,
      //   valueGetter: (
      //     params: GridValueGetterParams<undefined, FinancialReport>
      //   ) => `#${params.row?.site_number}`,
      // },
      {
        field: "start_date",
        headerName: "Start",
        minWidth: isMobile ? 140 : 230,
        headerAlign: "center",
        align: "center",
        valueGetter: (params: GridValueGetterParams) =>
          `${dayjs(params.row?.start_date).format("MMM DD, YYYY hh:mm A z")}`,
      },
      // {
      //   field: "percentage_uplift",
      //   headerName: "Company Uplift(%)",
      // },
      {
        field: "type",
        headerName: "Payment Type",
        minWidth: isMobile ? 100 : 120,
        headerAlign: "center",
        align: "center",
        valueGetter: (params: GridValueGetterParams) => `${params.row.type?.toUpperCase()}`,
      },
      {
        field: "talent_name",
        headerName: "Independent Professional",
        minWidth: isMobile ? 180 : 260,
        headerAlign: "center",
        align: "center",
        valueGetter: (params: GridValueGetterParams) => `#${params.row.talent_id} ${params.row.talent_name}`,
      },
      {
        field: "manager_name",
        headerName: "Manager",
        minWidth: isMobile ? 100 : 150,
        headerAlign: "center",
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          const project = dataProjects?.data.find(
            (dataProject) => dataProject.id === params.row.job_id
          );
          if (project?.manager === null) {
            // console.log(project);
            return "-";
          }
          return isLoadingDataProjects
            ? "loading..."
            : `${project?.manager?.full_name}`;
        },
      },
      {
        field: "price",
        headerName: "Rate/Price",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 90 : 120,
        valueGetter: (params: GridValueGetterParams) =>
          `${params.row.type == "hourly"
            ? `$${params.row.rate_hour || 0}/hr`
            : `$${params.row.fix_price || 0}`
          }`,
      },
      {
        field: "hours",
        headerName: "Est. Hours",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 80 : 100,
        valueGetter: (params: GridValueGetterParams) => `${params.row.approximate_hours_to_complete}`,
      },
      {
        field: "total_working",
        headerName: "Hours On Site",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 90 : 120,
      },
      {
        field: "total_earning",
        headerName: "Labor Cost",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 90 : 120,
        valueGetter: (params: GridValueGetterParams) => `$${Number(params.row.total_earning)?.toFixed(2) || "0"}`,
      },
      {
        field: "total_project_uplift",
        headerName: "Ticket Fee",
        align: "right",
        minWidth: isMobile ? 80 : 100,
        valueGetter: (params: GridValueGetterParams) => `${params.row.total_project_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "total_additional_hours",
        headerName: "Additional Hours",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 100 : 140,
        valueGetter: (params: GridValueGetterParams) => `$${params.row.total_additional_hours?.toFixed(2) || "0"}`,
      },
      {
        field: "total_additional_hours_uplift",
        headerName: "AH-Fee",
        align: "right",
        minWidth: isMobile ? 70 : 90,
        valueGetter: (params: GridValueGetterParams) => `${params.row.total_additional_hours_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "total_reimbursement",
        headerName: "Reimbursement",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 100 : 130,
        valueGetter: (params: GridValueGetterParams) => `$${params.row.total_reimbursement?.toFixed(2) || "0"}`,
      },
      {
        field: "total_reimbursement_uplift",
        headerName: "R-Fee",
        align: "right",
        minWidth: isMobile ? 70 : 90,
        valueGetter: (params: GridValueGetterParams) => `${params.row.total_reimbursement_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "total_bonus",
        headerName: "Bonus",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 80 : 100,
        valueGetter: (params: GridValueGetterParams) => `$${params.row.total_bonus?.toFixed(2) || "0"}`,
      },
      {
        field: "total_bonus_uplift",
        headerName: "B-Fee",
        align: "right",
        minWidth: isMobile ? 70 : 90,
        valueGetter: (params: GridValueGetterParams) => `${params.row.total_bonus_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "income",
        headerName: "P-Fees",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 80 : 100,
        valueGetter: (params: GridValueGetterParams) => {
          const income =
            Number(params.row.fixed_price_fee || 0) +
            Number(params.row.total_project_uplift || 0) +
            Number(params.row.total_additional_hours_uplift || 0) +
            Number(params.row.total_reimbursement_uplift || 0) +
            Number(params.row.total_bonus_uplift || 0);
          return `$${income.toFixed(2) || ""}`;
        },
      },
      // {
      //   field: "payroll",
      //   headerName: "Payments",
      //   align: "right",
      //   minWidth: 120,
      //   valueGetter: (
      //     params: GridValueGetterParams<undefined, FinancialReport>
      //   ) =>
      //     `$${
      //       Number(
      //         (params.row.total_earning || 0) +
      //           (params.row.total_bonus || 0) +
      //           (params.row.total_reimbursement || 0) +
      //           (params.row.total_additional_hours || 0)
      //       )?.toFixed(2) || ""
      //     }`,
      // },
      {
        field: "payroll",
        headerName: "Total Cost",
        headerAlign: "center",
        align: "center",
        minWidth: isMobile ? 90 : 120,
        valueGetter: (params: GridValueGetterParams) => {
          const totalEarning = Number(params.row.total_earning) || 0;
          const totalBonus = Number(params.row.total_bonus) || 0;
          const totalReimbursement =
            Number(params.row.total_reimbursement) || 0;
          const totalAdditionalHours =
            Number(params.row.total_additional_hours) || 0;
          const total_project_uplift = Number(params.row.fixed_price_fee) || 0;

          const total =
            totalEarning +
            totalBonus +
            totalReimbursement +
            // totalAdditionalHours +
            total_project_uplift;

          return `$${total.toFixed(2)}`;
        },
      },
      // {
      //   field: "gross_revenue",
      //   headerName: "G-Revenue",
      //   align: "right",
      //   minWidth: 120,
      //   valueGetter: (
      //     params: GridValueGetterParams<undefined, FinancialReport>
      //   ) =>
      //     `$${
      //       Number(
      //         (params.row.total_earning || 0) +
      //           (params.row.total_bonus || 0) +
      //           (params.row.total_reimbursement || 0) +
      //           (params.row.total_additional_hours || 0) +
      //           (params.row.fixed_price_fee || 0)
      //       )?.toFixed(2) || ""
      //     }`,
      // },
      // {
      //   field: "income",
      //   headerName: "Platform Fee",
      //   align: "right",
      //   minWidth: 100,
      //   valueGetter: (
      //     params: GridValueGetterParams<undefined, FinancialReport>
      //   ) =>
      //     `${
      //       Number(
      //         params.row.total_project_uplift +
      //           params.row.total_bonus_uplift +
      //           params.row.total_reimbursement_uplift +
      //           params.row.total_additional_hours_uplift
      //       )?.toFixed(2) || ""
      //     }`,
      // },
    ],
    [dataProjects, isMobile]
  );

  const handleFilter = React.useCallback(
    (start: Dayjs | Date, end: Dayjs | Date) => {
      setFilterDate([start, end]);
    },
    []
  );

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query?.toLowerCase() || "");
  }, []);

  const CustomToolbar = React.useMemo(
    () => () => <TableToolbar onFilter={handleFilter} onSearch={handleSearch} />,
    [handleFilter, handleSearch]
  );

  return (
    <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
      <CardBase
        title="Finance Report"
        useGlassCard={true}
        nested={false}
      >
        <GlassDataGrid
          height={isMobile ? "75vh" : "85vh"}
          rows={filteredRows}
          columns={columns}
          rowCount={filteredRows.length}
          pageSize={pageSize as any}
          rowsPerPageOptions={[100]}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          sortModel={sortModel}
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching || isLoadingDataProjects}
          slots={{
            loadingOverlay: LinearProgress,
            noRowsOverlay: NoResultsOverlay,
            toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          rowHeight={isMobile ? 56 : 64}
          sx={{
            "& .MuiDataGrid-toolbarContainer": {
              padding: { xs: "12px 16px", sm: "16px 20px" },
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(15px) saturate(180%)",
              WebkitBackdropFilter: "blur(15px) saturate(180%)",
              borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#12903C",
              },
            },
            "& .MuiDataGrid-cell": {
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
          }}
        />
      </CardBase>
    </Box>
  );
};
export default FinanceReportPage;
